import type { Theme } from "@mui/material/styles";

import ChevronRight from "@mui/icons-material/ChevronRight";
import Close from "@mui/icons-material/Close";
import Lock from "@mui/icons-material/Lock";
import {
  alpha,
  Box,
  ButtonBase,
  Chip,
  Collapse,
  Divider,
  IconButton,
  Paper,
  Stack,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { getScoutTierChipSx, SCOUT_TIER_CHIP_STYLES } from "components/scoutTierChipStyles";
import {
  SCOUT_POSTHOG_EVENTS,
  SCOUT_UPSELL_PLACEMENTS,
} from "encamp-shared/src/instrumentation/events";
import { useLayoutEffect, useMemo, useRef, useState, type RefObject } from "react";

import { capturePosthog } from "../../../../instrumentation/posthog";
import { scoutHostToRuntime } from "../../../../instrumentation/scoutHost";
import { SCOUT_ACCESS_PACKAGES, useScoutAccessPackage } from "../scoutAccess";
import { SCOUT_SUPPORT_ARTICLE } from "../ScoutInfoPanel";
import { useScoutHostContext } from "./ScoutHostContext";
import {
  getScoutSuggestionDisplayText,
  getVisibleScoutSuggestionCategories,
  MY_PROGRAM_CATEGORY_KEY,
  type ScoutSuggestion,
  type ScoutSuggestionCategory,
} from "./suggestions";

/** Preferred cap for the scrollable list when there's plenty of room below it. */
const DEFAULT_MAX_LIST_HEIGHT = 360;
/** Gap between the expanded list and the support footer (or viewport edge). */
const LIST_BOTTOM_GAP_PX = 16;
const ICON_ONLY_CHIP_SIZE_PX = 38;
/** Marks the NewChat support footer so the expanded list can stop above it. */
export const SCOUT_SUPPORT_FOOTER_ATTR = "data-scout-support-footer";

const UNLOCK_WITH_SCOUT_PRO_LABEL = "Unlock with Scout Pro";
const REGULATORY_ADVISOR_ACCENT = SCOUT_TIER_CHIP_STYLES["regulatory-advisor"].color;
function getSuggestionCategoryChipSx({
  selected,
  iconOnly,
  theme,
  accentColor,
}: {
  selected: boolean;
  iconOnly: boolean;
  theme: Theme;
  /** When set (Regulatory Advisor "My Program"), match that tier's orange chrome. */
  accentColor?: string;
}) {
  const iconOnlyLabelSx = iconOnly
    ? {
        pl: 0,
        pr: 0,
        "& .MuiChip-label": {
          display: "none",
          width: 0,
          p: 0,
        },
      }
    : {};

  if (accentColor) {
    return {
      height: ICON_ONLY_CHIP_SIZE_PX,
      px: iconOnly ? 0 : 0.5,
      width: iconOnly ? ICON_ONLY_CHIP_SIZE_PX : undefined,
      minWidth: iconOnly ? ICON_ONLY_CHIP_SIZE_PX : undefined,
      justifyContent: iconOnly ? "center" : undefined,
      fontWeight: 500,
      fontSize: "0.875rem",
      color: accentColor,
      borderColor: accentColor,
      backgroundColor: selected ? alpha(accentColor, 0.16) : alpha(accentColor, 0.08),
      "& .MuiChip-icon": {
        margin: iconOnly ? 0 : undefined,
        color: accentColor,
      },
      ...iconOnlyLabelSx,
      "&:hover": {
        backgroundColor: alpha(accentColor, selected ? 0.22 : 0.14),
      },
    };
  }

  return {
    height: ICON_ONLY_CHIP_SIZE_PX,
    px: iconOnly ? 0 : 0.5,
    width: iconOnly ? ICON_ONLY_CHIP_SIZE_PX : undefined,
    minWidth: iconOnly ? ICON_ONLY_CHIP_SIZE_PX : undefined,
    justifyContent: iconOnly ? "center" : undefined,
    fontWeight: 500,
    fontSize: "0.875rem",
    borderColor: selected ? "transparent" : "divider",
    color: selected ? theme.palette.primary.main : "text.primary",
    backgroundColor: selected ? alpha(theme.palette.primary.main, 0.1) : "transparent",
    "& .MuiChip-icon": {
      margin: iconOnly ? 0 : undefined,
      color: selected ? theme.palette.primary.main : "text.secondary",
    },
    ...iconOnlyLabelSx,
    "&:hover": {
      backgroundColor: selected
        ? alpha(theme.palette.primary.main, 0.16)
        : theme.palette.action.hover,
    },
  };
}

function SuggestionCategoryChip({
  category,
  selected,
  disabled,
  iconOnly,
  accentColor,
  onToggle,
}: {
  category: ScoutSuggestionCategory;
  selected: boolean;
  disabled: boolean;
  iconOnly: boolean;
  accentColor?: string;
  onToggle: () => void;
}) {
  const theme = useTheme();
  const Icon = category.Icon;

  return (
    <Chip
      icon={<Icon />}
      label={category.label}
      variant="outlined"
      clickable
      disabled={disabled}
      aria-expanded={selected}
      aria-label={category.label}
      onClick={onToggle}
      sx={getSuggestionCategoryChipSx({ selected, iconOnly, theme, accentColor })}
    />
  );
}

/** Cap the scroll region to space above the support footer / viewport. */
function useSuggestionListMaxHeight({
  activeKey,
  listRef,
}: {
  activeKey: string | null;
  listRef: RefObject<HTMLDivElement | null>;
}) {
  const [maxListHeight, setMaxListHeight] = useState(DEFAULT_MAX_LIST_HEIGHT);

  useLayoutEffect(() => {
    if (!activeKey) {
      return;
    }
    const updateMaxHeight = () => {
      const node = listRef.current;
      if (!node) {
        return;
      }
      const { top } = node.getBoundingClientRect();
      const supportFooter = document.querySelector(`[${SCOUT_SUPPORT_FOOTER_ATTR}]`);
      const bottomBound = supportFooter?.getBoundingClientRect().top ?? window.innerHeight;
      const available = bottomBound - top - LIST_BOTTOM_GAP_PX;
      setMaxListHeight(Math.max(0, Math.min(DEFAULT_MAX_LIST_HEIGHT, available)));
    };
    updateMaxHeight();
    window.addEventListener("resize", updateMaxHeight);
    return () => window.removeEventListener("resize", updateMaxHeight);
  }, [activeKey, listRef]);

  return maxListHeight;
}

function SuggestionListItem({
  item,
  disabled,
  onPick,
}: {
  item: ScoutSuggestion;
  disabled: boolean;
  onPick: (prompt: string) => void;
}) {
  return (
    <ButtonBase
      disabled={disabled}
      onClick={() => onPick(item.prompt)}
      sx={{
        width: "100%",
        justifyContent: "space-between",
        textAlign: "left",
        gap: 1.5,
        px: 2,
        py: 1.5,
        "&:hover": { backgroundColor: "action.hover" },
        "& .suggestion-chevron": { opacity: 0, transition: "opacity 120ms ease" },
        "&:hover .suggestion-chevron": { opacity: 1 },
      }}
    >
      <Typography variant="body2" sx={{ color: "text.secondary" }}>
        {getScoutSuggestionDisplayText(item)}
      </Typography>
      <ChevronRight
        className="suggestion-chevron"
        fontSize="small"
        sx={{ flexShrink: 0, color: "text.secondary", alignSelf: "center" }}
      />
    </ButtonBase>
  );
}

function SuggestionPanelHeader({
  category,
  accentColor,
  showProUpsellChip,
  onClose,
}: {
  category: ScoutSuggestionCategory;
  accentColor?: string;
  showProUpsellChip: boolean;
  onClose: () => void;
}) {
  const Icon = category.Icon;
  const { host } = useScoutHostContext();

  return (
    <>
      <Stack direction="row" alignItems="center" spacing={1} sx={{ px: 2, py: 1.25 }}>
        <Icon fontSize="small" sx={{ color: accentColor ?? "text.secondary" }} />
        <Typography variant="subtitle2" sx={{ flex: 1 }}>
          {category.label}
        </Typography>
        {showProUpsellChip ? (
          <Chip
            component="a"
            href={SCOUT_SUPPORT_ARTICLE}
            target="_blank"
            rel="noopener noreferrer"
            size="small"
            icon={<Lock />}
            label={UNLOCK_WITH_SCOUT_PRO_LABEL}
            variant="outlined"
            clickable
            onClick={() => {
              capturePosthog(SCOUT_POSTHOG_EVENTS.PRO_UPSELL_LEARN_MORE_CLICKED, {
                host: scoutHostToRuntime(host),
                placement: SCOUT_UPSELL_PLACEMENTS.SUGGESTION_MENU_HEADER,
              });
            }}
            sx={{
              ...getScoutTierChipSx("regulatory-advisor"),
              "& .MuiChip-icon": {
                color: "inherit",
              },
            }}
          />
        ) : null}
        <IconButton size="small" onClick={onClose} aria-label="Close suggestions">
          <Close fontSize="small" />
        </IconButton>
      </Stack>
      <Divider />
    </>
  );
}

function SuggestionPanel({
  category,
  showHeader,
  disabled,
  maxListHeight,
  listRef,
  accentColor,
  showProUpsellChip,
  onClose,
  onPick,
}: {
  category: ScoutSuggestionCategory;
  showHeader: boolean;
  disabled: boolean;
  maxListHeight: number;
  listRef: RefObject<HTMLDivElement | null>;
  accentColor?: string;
  showProUpsellChip: boolean;
  onClose: () => void;
  onPick: (prompt: string) => void;
}) {
  return (
    <Paper variant="outlined" sx={{ borderRadius: 2, overflow: "hidden" }}>
      {showHeader ? (
        <SuggestionPanelHeader
          category={category}
          accentColor={accentColor}
          showProUpsellChip={showProUpsellChip}
          onClose={onClose}
        />
      ) : null}
      <Box ref={listRef} sx={{ maxHeight: maxListHeight, overflowY: "auto" }}>
        {category.items.map((item, index) => (
          <Box key={item.prompt}>
            {index > 0 ? <Divider /> : null}
            <SuggestionListItem item={item} disabled={disabled} onPick={onPick} />
          </Box>
        ))}
      </Box>
    </Paper>
  );
}

/**
 * Progressive-disclosure starter prompts: a row of category chips that expand to
 * reveal the actual questions. Picking a question sends it to Scout.
 *
 * The expanded panel is absolutely positioned so opening/switching categories
 * never reflows the surrounding layout (no scroll jump).
 */
export function SuggestionMenu({
  onPick,
  disabled = false,
  showHeader = true,
}: {
  onPick: (prompt: string) => void;
  disabled?: boolean;
  /** Whether the expanded panel shows the category title and close control. */
  showHeader?: boolean;
}) {
  const theme = useTheme();
  // My Program (RA upsell) makes five labeled chips; collapse earlier than the
  // default "sm" mobile breakpoint so labels don't crowd/wrap.
  const iconOnlyChips = useMediaQuery(theme.breakpoints.down("md"));
  const { scoutAccessPackage } = useScoutAccessPackage();
  const isRegulatoryAdvisor = scoutAccessPackage === SCOUT_ACCESS_PACKAGES.REGULATORY_ADVISOR;
  // Sample Pro prompts once per access package so expanding/collapsing chips
  // does not reshuffle the list on every render.
  const visibleCategories = useMemo(
    () =>
      getVisibleScoutSuggestionCategories({
        audience: isRegulatoryAdvisor ? "regulatory-advisor" : "pro",
      }),
    [isRegulatoryAdvisor]
  );
  const [activeKey, setActiveKey] = useState<string | null>(null);
  const active = visibleCategories.find((category) => category.key === activeKey) ?? null;
  const listRef = useRef<HTMLDivElement | null>(null);
  const maxListHeight = useSuggestionListMaxHeight({ activeKey, listRef });
  const isMyProgramUpsell = active?.key === MY_PROGRAM_CATEGORY_KEY;

  return (
    <Box sx={{ position: "relative", width: "100%", maxWidth: 720 }}>
      <Stack
        direction="row"
        spacing={1.5}
        flexWrap="nowrap"
        justifyContent="center"
        sx={{ maxWidth: "100%" }}
      >
        {visibleCategories.map((category) => {
          const selected = category.key === activeKey;
          const accentColor =
            category.key === MY_PROGRAM_CATEGORY_KEY ? REGULATORY_ADVISOR_ACCENT : undefined;
          return (
            <SuggestionCategoryChip
              key={category.key}
              category={category}
              selected={selected}
              disabled={disabled}
              iconOnly={iconOnlyChips}
              accentColor={accentColor}
              onToggle={() => setActiveKey(selected ? null : category.key)}
            />
          );
        })}
      </Stack>

      <Box sx={{ position: "absolute", top: "100%", left: 0, right: 0, mt: 1.5, zIndex: 5 }}>
        <Collapse in={Boolean(active)} unmountOnExit>
          {active ? (
            <SuggestionPanel
              category={active}
              showHeader={showHeader}
              disabled={disabled}
              maxListHeight={maxListHeight}
              listRef={listRef}
              accentColor={isMyProgramUpsell ? REGULATORY_ADVISOR_ACCENT : undefined}
              showProUpsellChip={isMyProgramUpsell}
              onClose={() => setActiveKey(null)}
              onPick={onPick}
            />
          ) : null}
        </Collapse>
      </Box>
    </Box>
  );
}
